import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import Callout from '@dashboard/components/Callout';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import { chargeTicketsKey } from '@dashboard/lib/queryKeys';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import Button from '@/dashboard/components/Button';
import clsx from 'clsx';
import Badge from '@/dashboard/components/Badge';
import { CheckCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { useToast } from '@/dashboard/context/ToastContext';

const PaymentsBookingsDetailTickets = ({ payment }) => {
  const { sellerId, token } = useAuth();
  const { emitToast } = useToast();
  const { data, isLoading, isError } = useQuery({
    queryKey: chargeTicketsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/tickets/${payment._id}`, token),
    enabled: !!payment?._id,
  });

  const { mutateAsync: downloadTicketPDF, isPending: isDownloadingTicketPDF } = useMutation({
    mutationFn: async () => {
      return await fetcher(`/sellers/${sellerId}/tickets/${payment._id}/pdf`, token, {
        method: 'get',
      });
    },
    onSuccess: data => {
      if (data.pdfUrl == null) {
        emitToast({
          title: 'Failed to download ticket PDF',
          type: 'error',
        });
        return;
      }

      const link = document.createElement('a');
      link.href = data.pdfUrl;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: () => {
      emitToast({
        title: 'Failed to download ticket PDF',
        type: 'error',
      });
    },
  });

  const hasPdf =
    payment.page._id === '681aac984e3f8ba880f636f8' ||
    (payment?.createdAt && new Date(payment.createdAt) > new Date('2025-05-12T08:35:07.000Z'));

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader>
            <CardHeaderTitle>Tickets</CardHeaderTitle>
            <div className="flex items-center gap-2">
              {hasPdf && (
                <Button
                  appearance="secondary"
                  size="small"
                  className={clsx('flex items-center gap-x-2')}
                  onClick={downloadTicketPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Ticket PDF
                  {isDownloadingTicketPDF ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent>
            {isLoading && <Spinner />}

            {isError && <Callout variant="error">Failed to load tickets</Callout>}

            {data && data.tickets?.length === 0 && !isLoading && !isError && (
              <Callout variant="info">No tickets found for this payment.</Callout>
            )}

            {data && data.tickets?.length > 0 && (
              <div className="overflow-auto">
                <CardListItem variant="header" className="!mt-0">
                  <CardListItemElement size="xl" className="!pl-0">
                    Attendee name
                  </CardListItemElement>
                  <CardListItemElement size="3xl">Attendee email</CardListItemElement>
                  <CardListItemElement align="right" size="fill">
                    Ticket type
                  </CardListItemElement>
                  <CardListItemElement size="large">Ticket code</CardListItemElement>
                  <CardListItemElement size="xl">Ordered at</CardListItemElement>
                  <CardListItemElement size="medium">Status</CardListItemElement>
                  <CardListItemElement size="medium">Attendance</CardListItemElement>
                </CardListItem>

                {data.tickets.map(ticket => (
                  <CardListItem key={ticket._id}>
                    <CardListItemElement size="xl" className="!pl-0">
                      {ticket.customerName}
                    </CardListItemElement>
                    <CardListItemElement size="3xl">{ticket.customerEmail}</CardListItemElement>
                    <CardListItemElement align="right" size="fill">
                      {ticket.ticketType?.name}
                    </CardListItemElement>
                    <CardListItemElement size="large">{ticket.ticketShortId}</CardListItemElement>

                    <CardListItemElement size="xl">
                      {format(new Date(ticket.orderedAt), 'd MMM, yyyy, HH:mm')}
                    </CardListItemElement>

                    <CardListItemElement size="medium">
                      <Badge appearance="success">{upperFirst(ticket.status.toLowerCase())}</Badge>
                    </CardListItemElement>

                    <CardListItemElement size="medium">
                      {ticket.latestCheckIn != null &&
                      ticket.latestCheckIn.status === 'CHECKEDIN' ? (
                        <Badge appearance="success">
                          <CheckCircleIcon className="h-3 w-3 mr-1" aria-hidden="true" />
                          Checked in
                        </Badge>
                      ) : (
                        <Badge appearance="info">Pending</Badge>
                      )}
                    </CardListItemElement>
                  </CardListItem>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentsBookingsDetailTickets;
